import React, { useState, useEffect } from 'react';
import { genericosService } from '../../../services/genericos.service';
import { requisicionesService } from '../../../services/requisiciones.service';
import { useToast } from '../../../components/base/ToastContainer';

interface CrearPrincipioActivoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (data: any) => void;
}

export default function CrearPrincipioActivoModal({ isOpen, onClose, onSuccess }: CrearPrincipioActivoModalProps) {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [formasFarmaceuticas, setFormasFarmaceuticas] = useState<any[]>([]);

    const [formData, setFormData] = useState({
        nombre_sustancia: '',
        concentracion: '',
        forma_farmaceutica: ''
    });

    useEffect(() => {
        if (isOpen) {
            loadFormasFarmaceuticas();
        } else {
            // Reset form when closed
            setFormData({
                nombre_sustancia: '',
                concentracion: '',
                forma_farmaceutica: ''
            });
        }
    }, [isOpen]);

    const loadFormasFarmaceuticas = async () => {
        try {
            const data = await genericosService.getFormasFarmaceuticas();
            setFormasFarmaceuticas(data);
        } catch (error) {
            console.error('Error al cargar formas farmacéuticas:', error);
            showToast('Error al cargar lista de formas farmacéuticas', 'error');
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.nombre_sustancia.trim() || !formData.concentracion.trim() || !formData.forma_farmaceutica) {
            showToast('Todos los campos son obligatorios', 'error');
            return;
        }

        setIsLoading(true);
        try {
            // Obtener el ID del usuario actual
            let createdBy = '';
            try {
                const userStored = localStorage.getItem('user');
                if (userStored) {
                    const user = JSON.parse(userStored);
                    createdBy = user.id || '';
                }
            } catch (e) {
                console.error("Error obteniendo usuario para created_by", e);
            }

            const payload = {
                ...formData,
                created_by: createdBy
            };

            const result = await requisicionesService.createPrincipioActivo(payload) as any;
            showToast('Principio activo creado exitosamente', 'success');
            onSuccess(result?.data || result || formData);
            onClose();
        } catch (error: any) {
            console.error('Error al crear principio activo:', error);
            showToast(error.message || 'Error al crear principio activo', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}></div>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md relative z-10 flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <i className="ri-add-circle-line text-teal-600"></i>
                        Nuevo Principio Activo
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                        <i className="ri-close-line text-xl"></i>
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <form id="crear-principio-form" onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Nombre Sustancia *
                            </label>
                            <input
                                type="text"
                                name="nombre_sustancia"
                                value={formData.nombre_sustancia}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                                placeholder="Ej: Paracetamol"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Concentración *
                            </label>
                            <input
                                type="text"
                                name="concentracion"
                                value={formData.concentracion}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                                placeholder="Ej: 500 MG"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Forma Farmacéutica *
                            </label>
                            <select
                                name="forma_farmaceutica"
                                value={formData.forma_farmaceutica}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                            >
                                <option value="">-- Seleccione una forma --</option>
                                {formasFarmaceuticas.map((forma: any) => {
                                    const label = forma.nombre || forma.descripcion || forma.descForma || forma.id_forma;
                                    return (
                                        <option key={forma.id || forma.id_forma || forma.nombre} value={label}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </form>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 rounded-b-xl">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-slate-600 hover:bg-slate-200 bg-slate-100 rounded-lg transition-colors text-sm font-medium"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="crear-principio-form"
                        disabled={isLoading}
                        className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isLoading ? (
                            <><i className="ri-loader-4-line animate-spin"></i> Guardando...</>
                        ) : (
                            <><i className="ri-save-line"></i> Guardar</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
